"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.attachTooltipBehavior = attachTooltipBehavior;
exports.initPreactTooltips = initPreactTooltips;

var _experimentalSettings = require("@insided/shared/services/experimental-settings");

var _dom = require("@floating-ui/dom");

/**
 * Twig → Preact Tooltip bridge.
 *
 * ⚠️ This is a temporary workaround. See `./AGENTS.md` for the rationale,
 * when NOT to use this pattern, and the removal plan.
 *
 * Activates tooltip behavior on server-rendered twig DOM that uses
 * `data-tooltip-preact` containers with `.js-tooltip-trigger` /
 * `.js-tooltip-content` children. We attach event listeners and ARIA
 * directly to those existing DOM nodes instead of re-rendering them via
 * Preact — twig sites have framework attributes on the trigger element
 * itself (`data-component="subscribe"`, `role="radio"`, etc.) that must
 * survive the mount.
 *
 * For NEW Preact code that needs a tooltip, use `<Tooltip>` from
 * `@insided/destination/uikit/Tooltip` directly — do not call this bridge.
 */
var EXPERIMENTAL_FLAG = 'preactTooltipMigration';
var CONTAINER_SELECTOR = '[data-tooltip-preact]';
var TRIGGER_SELECTOR = '.js-tooltip-trigger';
var CONTENT_SELECTOR = '.js-tooltip-content';
var MEDIUM_SCREEN_PX = 768;
var CLOSE_DELAY_MS = 200;
var OFFSET_PX = 0;
var SHIFT_PADDING_PX = 14;
var POSITION_CLASSES = ['position--top', 'position--bottom', 'position--left', 'position--right'];
var nextTooltipId = 0;

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MEDIUM_SCREEN_PX;
}
/**
 * Attach tooltip behavior to existing DOM elements without replacing them.
 *
 * Sets ARIA on the trigger and content, toggles `is-active` / `is-enabled` /
 * `position--*` classes during show/hide, and drives Floating UI positioning.
 * Other attributes on the elements (`data-component`, `role`, `tabindex`, …)
 * are left intact.
 *
 * @returns a cleanup function that removes all listeners, stops Floating UI,
 *   and clears any pending hide timer.
 */


function attachTooltipBehavior(triggerEl, contentEl) {
  var _options$triggerMode, _options$position, _options$fitInViewpor, _options$disableOnMob;

  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var triggerMode = (_options$triggerMode = options.triggerMode) !== null && _options$triggerMode !== void 0 ? _options$triggerMode : 'hover';
  var placement = (_options$position = options.position) !== null && _options$position !== void 0 ? _options$position : 'bottom';
  var fitInViewport = (_options$fitInViewpor = options.fitInViewport) !== null && _options$fitInViewpor !== void 0 ? _options$fitInViewpor : true;
  var disableOnMobile = (_options$disableOnMob = options.disableOnMobile) !== null && _options$disableOnMob !== void 0 ? _options$disableOnMob : false;
  nextTooltipId += 1;
  var contentId = "preact-tooltip-".concat(nextTooltipId); // ARIA setup — applied once.

  triggerEl.setAttribute('aria-haspopup', 'true');
  triggerEl.setAttribute('aria-expanded', 'false'); // Preserve any pre-existing id on the content; otherwise assign ours.

  if (!contentEl.id) contentEl.id = contentId;
  triggerEl.setAttribute('aria-describedby', contentEl.id);
  if (!contentEl.getAttribute('role')) contentEl.setAttribute('role', 'tooltip');
  var visible = false;
  var hideTimer = null;
  var floatingCleanup = null;
  var docClickHandler = null;

  var startFloating = function startFloating() {
    if (floatingCleanup) floatingCleanup();
    var middleware = [(0, _dom.offset)(OFFSET_PX)];
    if (fitInViewport) middleware.push((0, _dom.flip)(), (0, _dom.shift)({
      padding: SHIFT_PADDING_PX
    }));
    floatingCleanup = (0, _dom.autoUpdate)(triggerEl, contentEl, function () {
      (0, _dom.computePosition)(triggerEl, contentEl, {
        placement: placement,
        middleware: middleware
      }).then(function (_ref) {
        var x = _ref.x,
            y = _ref.y,
            actual = _ref.placement;
        Object.assign(contentEl.style, {
          position: 'absolute',
          left: "".concat(x, "px"),
          top: "".concat(y, "px")
        });
        var side = actual.split('-')[0];
        POSITION_CLASSES.forEach(function (c) {
          return contentEl.classList.remove(c);
        });
        contentEl.classList.add("position--".concat(side));
      });
    });
  };

  var stopFloating = function stopFloating() {
    if (floatingCleanup) {
      floatingCleanup();
      floatingCleanup = null;
    }
  };

  var show = function show() {
    if (disableOnMobile && isMobileViewport()) return;

    if (hideTimer !== null) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }

    if (visible) return;
    visible = true;
    contentEl.classList.add('is-active', 'is-enabled');
    triggerEl.setAttribute('aria-expanded', 'true');
    if (triggerMode === 'click') triggerEl.classList.add('is-active');
    startFloating();

    if (triggerMode === 'click') {
      docClickHandler = onDocClick;
      document.addEventListener('click', docClickHandler);
    }
  };

  var hide = function hide() {
    if (!visible) return;
    visible = false;
    contentEl.classList.remove('is-active', 'is-enabled');
    triggerEl.setAttribute('aria-expanded', 'false');
    triggerEl.classList.remove('is-active');
    stopFloating();

    if (docClickHandler) {
      document.removeEventListener('click', docClickHandler);
      docClickHandler = null;
    }
  };

  var onEnter = function onEnter() {
    if (hideTimer !== null) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }

    show();
  };

  var onLeave = function onLeave() {
    if (hideTimer !== null) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(function () {
      hideTimer = null;
      hide();
    }, CLOSE_DELAY_MS);
  };

  var onTriggerClick = function onTriggerClick(e) {
    e.stopPropagation();
    if (visible) hide();else show();
  };

  function onDocClick(e) {
    var target = e.target;
    if (!target) return;
    if (triggerEl.contains(target) || contentEl.contains(target)) return;
    hide();
  }

  if (triggerMode === 'click') {
    triggerEl.addEventListener('click', onTriggerClick);
  } else {
    triggerEl.addEventListener('mouseenter', onEnter);
    triggerEl.addEventListener('mouseleave', onLeave);
    contentEl.addEventListener('mouseenter', onEnter);
    contentEl.addEventListener('mouseleave', onLeave);
  }

  return function cleanup() {
    triggerEl.removeEventListener('click', onTriggerClick);
    triggerEl.removeEventListener('mouseenter', onEnter);
    triggerEl.removeEventListener('mouseleave', onLeave);
    contentEl.removeEventListener('mouseenter', onEnter);
    contentEl.removeEventListener('mouseleave', onLeave);

    if (docClickHandler) {
      document.removeEventListener('click', docClickHandler);
      docClickHandler = null;
    }

    if (hideTimer !== null) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }

    stopFloating();
  };
}

function readTriggerMode(container) {
  return container.dataset.tooltipTriggerMode === 'click' ? 'click' : 'hover';
}

function readPosition(container) {
  var pos = container.dataset.tooltipPosition;
  if (pos === 'top' || pos === 'left' || pos === 'right') return pos;
  return 'bottom';
}

function readBoolean(container, key, defaultValue) {
  var raw = container.dataset[key];
  if (raw === undefined) return defaultValue;
  return raw === 'true';
}

function mountOne(container) {
  var triggerEl = container.querySelector(TRIGGER_SELECTOR);
  var contentEl = container.querySelector(CONTENT_SELECTOR); // Pagination sites have neither — the legacy Marionette mount was a no-op
  // there. Skip silently to preserve that behavior.

  if (!triggerEl || !contentEl) return;
  attachTooltipBehavior(triggerEl, contentEl, {
    triggerMode: readTriggerMode(container),
    position: readPosition(container),
    fitInViewport: readBoolean(container, 'tooltipFitInViewport', true),
    disableOnMobile: readBoolean(container, 'tooltipDisableOnMobile', false)
  });
}
/**
 * Mount the twig tooltip bridge. Called once from `initializeApp.ts`.
 *
 * No-op when the `experimental_preactTooltipMigration` URL flag is not set
 * — the legacy Marionette `Tooltip` view stays mounted on `data-view="Tooltip"`
 * containers, this bridge does nothing.
 */


function initPreactTooltips() {
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  if (!_experimentalSettings.experimental.has(EXPERIMENTAL_FLAG)) return;
  root.querySelectorAll(CONTAINER_SELECTOR).forEach(mountOne);
}